"use client";

/**
 * The contest page's Season tab: `EX-INS01`, `docs/29` §6.25. One table per
 * published season this round is in (seldom more than one), each read from
 * the season's own public route, with a link to the season's page.
 */
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowUpRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber } from "@/lib/format-datetime";
import { usePublicSeason } from "../../hooks";
import type { ContestPageSeason } from "../../types";
import { SeasonTable } from "./SeasonTable";

function OneSeason({ season, locale, contestId }: { season: ContestPageSeason; locale: string; contestId: string }) {
  const t = useTranslations("contest.season");
  const query = usePublicSeason(season.slug);
  if (query.isLoading) return <Skeleton className="h-72 rounded-2xl" />;
  if (query.isError || !query.data) {
    return (
      <div className="rounded-2xl border border-[rgb(var(--border))] bg-white p-6 text-center shadow-card" role="alert">
        <p className="text-sm text-neutral-700">{t("loadFailed")}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void query.refetch()}>
          {t("retry")}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <span className="text-[13px] font-medium text-neutral-500">
          {t("roundOf", { round: formatNumber(season.round, locale), rounds: formatNumber(season.rounds, locale) })}
        </span>
        <Button asChild variant="link" size={null} className="text-[13px] font-semibold">
          <Link href={`/${locale}/contests/seasons/${season.slug}`} data-testid="season-page-link">
            {t("openPage")}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
      </div>
      <SeasonTable season={query.data} locale={locale} highlightContestId={contestId} />
    </div>
  );
}

export function SeasonPanel({
  seasons,
  locale,
  contestId,
}: {
  seasons: ContestPageSeason[];
  locale: string;
  contestId: string;
}) {
  return (
    <div className="flex flex-col gap-6" data-testid="season-panel">
      {seasons.map((season) => (
        <OneSeason key={season.slug} season={season} locale={locale} contestId={contestId} />
      ))}
    </div>
  );
}
